import { state } from "./state";
import { RADIUS } from "./levels";
import { covers, key } from "./geometry";
import { makeNoriaEl } from "./norias";
import { circlesEl, gridEl, noriasEl, stageEl, treesEl } from "../dom";

/** Cached cell elements and palm elements for the current level. */
export const cellEls: HTMLDivElement[][] = [];
export const treeEls: { r: number; c: number; el: HTMLDivElement; pic: HTMLSpanElement }[] = [];

/* ---------- Build ---------- */
export function buildGrid(): void {
  const L = state.level();
  gridEl.style.gridTemplateColumns = `repeat(${L.cols},var(--cell))`;
  gridEl.style.gridTemplateRows = `repeat(${L.rows},var(--cell))`;
  gridEl.innerHTML = "";
  cellEls.length = 0;
  for (let r = 0; r < L.rows; r++) {
    const row: HTMLDivElement[] = [];
    for (let c = 0; c < L.cols; c++) {
      const el = document.createElement("div");
      el.className = "cell";
      // subtle organic sand variation
      el.style.filter = `brightness(${(0.96 + Math.random() * 0.08).toFixed(3)})`;
      if (state.rockSet.has(key(r, c))) {
        el.classList.add("rock");
        const g = document.createElement("div");
        g.className = "rock-glyph";
        g.textContent = "🪨";
        el.appendChild(g);
      }
      gridEl.appendChild(el);
      row.push(el);
    }
    cellEls.push(row);
  }
}

export function buildTrees(): void {
  treesEl.innerHTML = "";
  treeEls.length = 0;
  for (const [r, c] of state.level().trees) {
    const el = document.createElement("div");
    el.className = "tree";
    const pic = document.createElement("span");
    pic.className = "pic";
    pic.textContent = "🌴";
    el.appendChild(pic);
    treesEl.appendChild(el);
    treeEls.push({ r, c, el, pic });
  }
}

/* ---------- Layout / size ---------- */
export function layout(): void {
  const L = state.level();
  const holder = document.querySelector(".map-holder") as HTMLElement;
  const holderW = holder?.clientWidth || 600;
  const availW = Math.min(holderW, window.innerWidth - 28);
  const availH = Math.max(260, window.innerHeight * 0.56);
  let cell = Math.floor((availW - 12) / L.cols);
  cell = Math.min(cell, Math.floor((availH - 12) / L.rows));
  cell = Math.max(26, Math.min(60, cell));
  state.cell = cell;
  document.documentElement.style.setProperty("--cell", cell + "px");
  const w = cell * L.cols, h = cell * L.rows;
  stageEl.style.width = w + "px";
  stageEl.style.height = h + "px";
  for (const layer of [circlesEl, treesEl, noriasEl]) {
    layer.style.width = w + "px";
    layer.style.height = h + "px";
  }
  positionTrees();
}

function positionTrees(): void {
  const cell = state.cell;
  for (const t of treeEls) {
    t.el.style.left = t.c * cell + "px";
    t.el.style.top = t.r * cell + "px";
    t.el.style.width = cell + "px";
    t.el.style.height = cell + "px";
    t.pic.style.fontSize = Math.round(cell * 0.72) + "px";
  }
}

/* ---------- Render norias + irrigation circles ---------- */
export function renderNorias(): void {
  noriasEl.innerHTML = "";
  circlesEl.innerHTML = "";
  const cell = state.cell, R = RADIUS;
  for (const n of state.norias) {
    const circ = document.createElement("div");
    circ.className = "irrig";
    const dia = 2 * R * cell;
    circ.style.width = dia + "px";
    circ.style.height = dia + "px";
    circ.style.left = (n.c * cell + cell / 2 - R * cell) + "px";
    circ.style.top = (n.r * cell + cell / 2 - R * cell) + "px";
    circlesEl.appendChild(circ);

    const size = cell * 1.28;
    const el = makeNoriaEl();
    el.dataset.id = String(n.id);
    el.style.width = size + "px";
    el.style.height = size + "px";
    el.style.left = (n.c * cell + cell / 2 - size / 2) + "px";
    el.style.top = (n.r * cell + cell / 2 - size / 2) + "px";
    if (state.drag && state.drag.id === n.id && state.drag.moved) el.classList.add("lifted");
    noriasEl.appendChild(el);
    n.el = el;
  }
}

/** Set of watered cells (any non-rock cell within some noria's reach). */
export function coverageSet(): Set<string> {
  const L = state.level();
  const cov = new Set<string>();
  for (let r = 0; r < L.rows; r++)
    for (let c = 0; c < L.cols; c++) {
      if (state.rockSet.has(key(r, c))) continue;
      for (const n of state.norias) {
        if (covers(n.r, n.c, r, c)) { cov.add(key(r, c)); break; }
      }
    }
  return cov;
}

/** Apply watered styling to cells + palms; bloom newly-revived palms. Returns
 *  the number of palms watered. */
export function applyCoverage(): number {
  const L = state.level();
  const cov = coverageSet();
  for (let r = 0; r < L.rows; r++)
    for (let c = 0; c < L.cols; c++)
      cellEls[r][c].classList.toggle("watered", cov.has(key(r, c)));

  const newAlive = new Set<string>();
  for (const t of treeEls) if (cov.has(key(t.r, t.c))) newAlive.add(key(t.r, t.c));
  for (const t of treeEls) {
    const k = key(t.r, t.c), on = newAlive.has(k);
    t.el.classList.toggle("alive", on);
    if (on && !state.aliveSet.has(k)) {
      t.el.classList.add("bloom");
      setTimeout(() => t.el.classList.remove("bloom"), 460);
    }
  }
  state.aliveSet = newAlive;
  return newAlive.size;
}
